/* =============================================================================
   VIEWS / ADMIN / ADMIN-ORDER-ENTRY-DELIVERY — Liefer-Pfad
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5 Cut 2/4
   ----------------------------------------------------------------------------
   Liefer-Pfad: Adress-Block + PLZ-Live-Check + Stammkunden-Lookup +
   Quick-Add-Zone-Modal (manager/owner-Inline-Workaround). Mutiert _state
   via ESM live binding aus admin-order-entry-state.js.

   Cross-Module:
     - Re-Render des Modals via window.renderAdminOrderEntryModal()
     - closeModal() für Quick-Add (direkt aus utils/dom.js)
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { closeModal } from '../../utils/dom.js';
import { _state } from './admin-order-entry-state.js';
import { renderMailOptInBlock, ORDER_MAIL_KINDS } from './mail-optin-block.js';

/** S209-HF2 (4) — Zonen-Banner (grün erkannt / rot nicht im Gebiet / leer) aus
 *  _state. Als eigenes Fragment, damit der PLZ-Check NUR dieses Element updaten
 *  kann, ohne den ganzen Adress-Block (und damit parallel getippte Eingaben wie
 *  die E-Mail) per outerHTML zu verwerfen. */
export function _zoneBannerHtml() {
  const a = _state.deliveryAddress;
  const z = _state.deliveryZone;
  if (z) {
    return `
    <div style="background:var(--green-soft);border-left:3px solid var(--green);padding:8px 12px;margin-top:6px;font-size:.88rem;">
      ✓ Liefergebiet erkannt: <strong>${htmlEscape(z.zone_name || '—')}</strong>
      · Liefergebühr <strong>${((z.fee_cents || 0) / 100).toFixed(2).replace('.', ',')} €</strong>
      · Mindestbestellwert <strong>${((z.min_order_cents || 0) / 100).toFixed(2).replace('.', ',')} €</strong>
    </div>`;
  }
  if (a.plz && a.plz.length >= 4) {
    return `
    <div style="background:var(--danger-soft);border-left:3px solid var(--danger);padding:8px 12px;margin-top:6px;font-size:.88rem;">
      ✕ PLZ <strong>${htmlEscape(a.plz)}</strong> ist nicht im Liefergebiet.
    </div>`;
  }
  return '';
}

// S60 Block D — Delivery-Adress-Block + PLZ-Live-Check via check_delivery_zone-RPC.
export function renderDeliveryAddressBlock() {
  const a = _state.deliveryAddress;
  const zoneInfoHtml = `<div id="admin-order-zone-banner">${_zoneBannerHtml()}</div>`;
  return `
    <div class="admin-order-delivery-block" style="background:var(--bg-soft);padding:12px;border-radius:8px;margin-bottom:8px;">
      <div style="font-weight:600;margin-bottom:8px;">📍 Lieferadresse</div>
      <div class="field-row">
        <div class="field"><label>Name *</label>
          <input type="text" value="${htmlEscape(a.name)}" oninput="adminOrderSetDeliveryField('name', this.value)" placeholder="Vorname Nachname"></div>
        <div class="field"><label>Telefon * <span style="font-size:.78rem;color:var(--ink-soft);font-weight:normal;">(Stammkunden-Lookup)</span></label>
          <input type="tel" value="${htmlEscape(a.phone)}" oninput="adminOrderSetDeliveryField('phone', this.value)" onblur="adminOrderDeliveryLookupCustomer()" placeholder="+49 …"></div>
      </div>
      ${_state.deliveryCustomerId ? `
        <div style="background:var(--green-soft);border-left:3px solid var(--green);padding:8px 12px;margin:6px 0;font-size:.88rem;">
          ✓ Stammkunde erkannt — Adresse wurde vorausgefüllt, Datenbank-Verknüpfung wird gespeichert.
        </div>` : ''}
      <div class="field-row">
        <div class="field" style="flex:2;"><label>Straße + Hausnummer *</label>
          <input type="text" value="${htmlEscape(a.street)}" oninput="adminOrderSetDeliveryField('street', this.value)" placeholder="Musterstraße 12"></div>
        <div class="field" style="flex:1;"><label>Etage</label>
          <input type="text" value="${htmlEscape(a.floor)}" oninput="adminOrderSetDeliveryField('floor', this.value)" placeholder="z.B. 2. OG"></div>
      </div>
      <div class="field-row">
        <div class="field" style="flex:1;"><label>PLZ *</label>
          <input type="text" value="${htmlEscape(a.plz)}" oninput="adminOrderSetDeliveryField('plz', this.value)" onblur="adminOrderCheckPlz()" placeholder="80331" maxlength="5"></div>
        <div class="field" style="flex:2;"><label>Stadt *</label>
          <input type="text" value="${htmlEscape(a.city)}" oninput="adminOrderSetDeliveryField('city', this.value)" placeholder="München"></div>
      </div>
      <div class="field"><label>Notiz für Fahrer</label>
        <input type="text" value="${htmlEscape(a.notes)}" oninput="adminOrderSetDeliveryField('notes', this.value)" placeholder="z.B. Klingelschild Mustermann, am Hinterhof"></div>
      ${zoneInfoHtml}
      ${renderMailOptInBlock({
        kinds: ORDER_MAIL_KINDS,
        email: _state.customerEmail,
        optIn: _state.mailOptIn,
        idPrefix: 'admorder',
        emailHandler: 'adminOrderSetCustomerEmail',
        toggleHandler: 'adminOrderSetMailOptIn',
        showToggles: false,    // S88 C: keine Checkboxen — Server leitet Opt-in ab
        emailRequired: true    // S88 B/C: Lieferung braucht E-Mail (Bestätigung + Storno-Link)
      })}
    </div>
  `;
}

export function adminOrderSetDeliveryField(field, value) {
  if (!Object.prototype.hasOwnProperty.call(_state.deliveryAddress, field)) return;
  _state.deliveryAddress[field] = String(value || '').trim();
  // S60 W4.7 — bei Telefon-/Mail-Änderung Stammkunden-Match zurücksetzen
  if ((field === 'phone' || field === 'email') && _state.deliveryCustomerId) {
    _state.deliveryCustomerId = null;
  }
}

/** S60 W4.7 — Stammkunden-Lookup im Liefer-Pfad analog Hier-Essen.
 *  Telefon-on-blur → customers.findByPhone → Name + Adresse vorausfüllen +
 *  customerId merken. Konsistenz zwischen den beiden Cockpit-Pfaden. */
export async function adminOrderDeliveryLookupCustomer() {
  const phone = (_state.deliveryAddress.phone || '').trim();
  if (!phone || phone.length < 4) return;
  if (!window.App || !window.App.storage || !window.App.storage.customers
      || typeof window.App.storage.customers.findByPhone !== 'function') return;
  try {
    const customer = await window.App.storage.customers.findByPhone(phone);
    if (customer && customer.id) {
      _state.deliveryCustomerId = customer.id;
      // Adresse aus default_delivery_address vorausfüllen, sofern vorhanden
      // und User die Felder noch nicht selbst gefüllt hat.
      const def = customer.default_delivery_address || customer.defaultDeliveryAddress;
      if (def && typeof def === 'object') {
        if (!_state.deliveryAddress.street && def.street)  _state.deliveryAddress.street = String(def.street);
        if (!_state.deliveryAddress.plz    && def.plz)     _state.deliveryAddress.plz    = String(def.plz);
        if (!_state.deliveryAddress.city   && def.city)    _state.deliveryAddress.city   = String(def.city);
        if (!_state.deliveryAddress.floor  && def.floor)   _state.deliveryAddress.floor  = String(def.floor);
      }
      if (!_state.deliveryAddress.name && customer.name) _state.deliveryAddress.name = String(customer.name);
      // Re-Render des Delivery-Blocks (non-destruktiv via block-replace)
      const block = document.querySelector('.admin-order-delivery-block');
      if (block) {
        block.outerHTML = renderDeliveryAddressBlock();
        // Nach Vorausfüllung: PLZ-Check direkt anstoßen, damit Liefergebiet-Banner sichtbar wird
        if (_state.deliveryAddress.plz && _state.deliveryAddress.plz.length >= 4) {
          adminOrderCheckPlz();
        }
      }
    }
  } catch (err) {
    console.warn('[delivery-lookup]', err);
  }
}

export async function adminOrderCheckPlz() {
  const plz = _state.deliveryAddress.plz;
  if (!plz || plz.length < 4) {
    _state.deliveryZone = null;
    return;
  }
  if (!window.App || !window.App.storage || !window.App.storage.deliveryZones) {
    console.warn('[admin-order-entry] deliveryZones-Adapter fehlt');
    return;
  }
  try {
    const zone = await window.App.storage.deliveryZones.checkPlz(plz);
    _state.deliveryZone = zone || null;
  } catch (err) {
    console.warn('[admin-order-entry] checkPlz:', err);
    _state.deliveryZone = null;
  }
  // S209-HF2 (4) — NUR das Zonen-Banner aktualisieren (grün/rot), nicht den ganzen
  // Adress-Block per outerHTML. So bleiben parallel getippte Eingaben (v.a. die
  // E-Mail) + Fokus/Cursor erhalten. Die Speisekarte ist ein eigener Step (5) und
  // wird beim „Weiter zur Speisekarte" gemountet, nicht hier
  // (Memory feedback_gg_non_destructive_render).
  const banner = document.getElementById('admin-order-zone-banner');
  if (banner) {
    banner.innerHTML = _zoneBannerHtml();
  }
}

/**
 * S61 — Quick-Add-Modal für Liefer-Zone, wenn der Operator eine Liefer-
 * Bestellung mit nicht abgedeckter PLZ aufnehmen will und manager/owner-
 * Berechtigung hat. Vermeidet operativen Show-Stopper, ohne dass ein
 * Admin/Owner extra in Settings → Lieferung wechseln muss.
 *
 * Permission-Gate auf Aufrufer-Seite — diese Funktion vertraut darauf,
 * dass `canEdit('settings.rules')` schon geprüft ist (siehe submitAdminOrder).
 */
export function _openQuickAddDeliveryZoneModal(plz) {
  const modalContent = document.getElementById('modal-content');
  if (!modalContent) {
    showToast('Modal-Container nicht verfügbar — Cockpit neu laden.', 'error');
    return;
  }

  // Bridge-Funktionen idempotent registrieren (CSP-Inline-Pattern, Memory
  // `feedback_gg_inline_handler_csp`: kein window.-Präfix in onclick-Strings).
  if (typeof window !== 'undefined') {
    window._closeQuickAddDeliveryZone = function () {
      try { closeModal(); } catch (_) {}
    };
    window._submitQuickAddDeliveryZone = async function () {
      const nameEl = document.getElementById('qaz-name');
      const feeEl  = document.getElementById('qaz-fee');
      const minEl  = document.getElementById('qaz-min');
      const name = (nameEl && nameEl.value || '').trim();
      const fee  = Number(feeEl && feeEl.value || 0);
      const min  = Number(minEl && minEl.value || 0);
      if (!name) {
        return showToast('Bitte Zonen-Name eingeben.', 'error');
      }
      if (!isFinite(fee) || fee < 0) {
        return showToast('Liefergebühr ungültig.', 'error');
      }
      if (!isFinite(min) || min < 0) {
        return showToast('Mindestbestellwert ungültig.', 'error');
      }
      try {
        const created = await window.App.storage.deliveryZones.upsert({
          name,
          plzList: [plz],
          feeCents: Math.round(fee * 100),
          minOrderCents: Math.round(min * 100),
          active: true
        });
        // Zone direkt in _state aktivieren, damit der Operator nicht nochmal
        // die PLZ abtippen muss.
        _state.deliveryZone = {
          zone_id: created && created.id ? created.id : null,
          name,
          fee_cents: Math.round(fee * 100),
          min_order_cents: Math.round(min * 100)
        };
        if (typeof window.audit === 'function') {
          window.audit('delivery_zone.created_inline', {
            zone_id: _state.deliveryZone.zone_id,
            name, plz,
            fee_cents: _state.deliveryZone.fee_cents,
            min_order_cents: _state.deliveryZone.min_order_cents,
            context: 'admin-order-entry quick-add'
          });
        }
        closeModal();
        showToast(`Zone „${name}" angelegt (PLZ ${plz}, ${fee.toFixed(2).replace('.',',')} €). Bitte jetzt auf „Weiter zur Speisekarte" klicken.`, 'success');
        // S209-HF2 — Operator klickt selbst nochmal „Weiter": wir reopenen den
        // admin-order-entry-Modal auf dem aktuellen Daten-Step (6) mit erhaltenem
        // State; die Zone ist jetzt gesetzt, der „Weiter"-Gate lässt durch.
        if (typeof window.renderAdminOrderEntryModal === 'function') window.renderAdminOrderEntryModal();
      } catch (err) {
        console.error('[quickAddDeliveryZone]', err);
        showToast('Zone konnte nicht angelegt werden: ' + (err.message || err), 'error');
      }
    };
  }

  const html = `
    <div style="padding: 20px 24px 22px;">
      <div style="font-size:0.92rem; color:var(--ink-soft); margin-bottom:6px;">⚠️ PLZ noch nicht im Liefergebiet</div>
      <h2 style="margin:0 0 8px 0; font-size:1.3rem;">Neue Liefer-Zone anlegen</h2>
      <div style="color:var(--ink-soft); font-size:0.92rem; margin-bottom:18px;">
        Für PLZ <strong>${htmlEscape(plz)}</strong> ist noch keine Zone angelegt.
        Du kannst sie jetzt direkt hinzufügen, damit die Bestellung durchgehen kann.
      </div>
      <div class="field">
        <label for="qaz-name">Zonen-Name *</label>
        <input id="qaz-name" type="text" placeholder="z.B. Innenstadt, Hamburg-West" required>
      </div>
      <div class="field-row">
        <div class="field">
          <label for="qaz-fee">Liefergebühr (€) *</label>
          <input id="qaz-fee" type="number" min="0" step="0.10" value="3.50" required>
        </div>
        <div class="field">
          <label for="qaz-min">Mindestbestellwert (€) *</label>
          <input id="qaz-min" type="number" min="0" step="1.00" value="20.00" required>
        </div>
      </div>
      <div style="color:var(--ink-soft); font-size:0.82rem; margin-bottom:18px; line-height:1.5;">
        Die Zone wird sofort aktiv und ist nach dem Anlegen auch in <em>Settings → Lieferung</em>
        änderbar. PLZ <strong>${htmlEscape(plz)}</strong> wird ihr automatisch zugewiesen.
      </div>
      <div style="display:flex; gap:10px; justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="_closeQuickAddDeliveryZone()">Abbrechen</button>
        <button class="btn btn-primary" onclick="_submitQuickAddDeliveryZone()">Zone anlegen + zurück zur Bestellung</button>
      </div>
    </div>
  `;
  modalContent.innerHTML = html;
  const overlay = document.getElementById('modal-overlay');
  if (overlay) overlay.classList.add('active');
  setTimeout(() => {
    const f = document.getElementById('qaz-name');
    if (f) f.focus();
  }, 60);
}
